import { createContext, useContext } from 'react';

/**
 * The creator's app renders at 90 % (THE_LIST #55, ruled 2026-10-23). Jota:
 * "Make it so inside contruly, the font sizes and size of everything in general
 * looks like 90% scale/zoom would look like. I keep having to zoom out to have
 * the desired sizes inside the app."
 *
 * How: <AppSurface> marks the app's routes (/dashboard, /auth, /partnerships)
 * with data-surface="app", and src/index.css sets the root font size to 90 %
 * while that mark is on the page and the pointer is a mouse or trackpad. Every
 * rem follows: text, spacing, controls, icons, corners. A client's pages (the
 * site, the wizard, the portal) are never inside the mark and keep 100 %; a
 * phone keeps 100 %, so a thumb target stays 44 px. Not CSS zoom: drag-and-drop
 * and the flow diagrams compare pointer positions with element boxes, and the
 * two disagree under zoom. Borders and shadows stay in pixels.
 *
 * This constant and the 90 % in index.css are one fact in two languages;
 * src/parity/appRendersAtNinetyPercent.parity.test.ts holds them equal.
 */
export const APP_SCALE = 0.9;

export const SurfaceScaleContext = createContext(1);

/**
 * The scale the root applies where this is read: APP_SCALE inside the app on a
 * mouse or trackpad, 1 everywhere else. For rules that judge rendered pixels
 * (the logo's hand-over and small cut), never for sizing: sizes are rem and
 * follow the root by themselves.
 */
export function useRenderedScale(): number {
  const scale = useContext(SurfaceScaleContext);
  if (scale === 1 || typeof window === 'undefined' || !window.matchMedia) return 1;
  return window.matchMedia('(pointer: fine)').matches ? scale : 1;
}
